import { Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import { TimeEntryActionBarContextProvider } from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBarContext';
import DashboardActiveList from 'components/foundation/dashboard/dashboard-list/DashboardActiveList/DashboardActiveList';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import ActivityDetailsDialog from 'containers/activity-reports/activity-report-details/ActivityDetailsDialog/ActivityDetailsDialog';
import { ActivityReportType } from 'containers/activity-reports/ActivityReportFilter/ActivityReportFilter';
import ChannelContextProvider from 'contexts/ChannelContext/ChannelContext';
import { useEmployeeDashboardNavigation, useOpennable } from 'hooks';
import { isNull } from 'lodash';
import { AnyObject } from 'types/util/Object';
import { colors } from 'utils/constants/colors';
import { t } from 'utils/localize';
import DashboardCardSpinner from '../DashboardCardSpinner/DashboardCardSpinner';
import DashboardErrorCard from '../DashboardErrorCard/DashboardErrorCard';
import useActiveCardReportNavigation from '../hooks/useActiveCardReportNavigation';
import useActivityReportDetailNavigation from '../hooks/useActivityReportDetailNavigation';
import useActiveMembersDashboard from './hooks/useActiveMembersDashboard';

export interface IActiveMembersDashboardCardProps {
  setting: IDashboardSettingsItem;
  theme: Theme.DARK | Theme.LIGHT;
  className?: string | AnyObject;
  projectId?: string | null;
  costCodeId?: string | null;
  equipmentId?: string | null;
}

const ActiveMembersDashboardCard = ({
  setting,
  theme,
  className,
  projectId,
  costCodeId,
  equipmentId,
}: IActiveMembersDashboardCardProps) => {
  const { data, loading, error, title, execute } = useActiveMembersDashboard(
    setting,
    projectId,
    costCodeId,
    equipmentId
  );

  const employeeDashboardNavigation = useEmployeeDashboardNavigation();
  const activityReportNavigation = useActiveCardReportNavigation(setting.options?.time, 'by-employee');
  const { timeRangeType, navRange, getActivityReportFilter, getActivityReportFilterType } =
    useActivityReportDetailNavigation(setting.options?.time, undefined, costCodeId, equipmentId, projectId);
  const activityReportDetailsDialog = useOpennable();

  function onRowClick(id: string) {
    employeeDashboardNavigation({ employee_id: id, exitToLegacy: false });
  }

  function renderList() {
    return data && data?.length > 0 ? (
      <DashboardActiveList rowItems={data} onRowClick={onRowClick} theme={theme} />
    ) : null;
  }

  function handleRightClick() {
    if (isNull(getActivityReportFilter())) {
      activityReportNavigation();
    } else {
      activityReportDetailsDialog.open();
    }
  }

  const classes = classNames('active-members-dashboard-card', className);

  return (
    <>
      {error === null ? (
        <DashboardContentCard
          subheaderValue={title}
          tabColor={colors.primary}
          theme={theme}
          className={classes}
          rightLabel={t('View Report')}
          onRightLabelClick={handleRightClick}
        >
          {loading ? <DashboardCardSpinner /> : renderList()}
        </DashboardContentCard>
      ) : (
        <DashboardErrorCard theme={theme} onClick={execute} />
      )}
      {!isNull(timeRangeType) && !isNull(navRange) && (
        <TimeEntryActionBarContextProvider>
          <ChannelContextProvider>
            <ActivityDetailsDialog
              isOpen={activityReportDetailsDialog.isOpen}
              onClose={activityReportDetailsDialog.close}
              timeRange={navRange}
              timeRangeType={timeRangeType}
              filterType={getActivityReportFilterType()}
              filterId={getActivityReportFilter()}
              initialReportType={ActivityReportType.BY_EMPLOYEE}
            />
          </ChannelContextProvider>
        </TimeEntryActionBarContextProvider>
      )}
    </>
  );
};

export default ActiveMembersDashboardCard;
